import { useState, useEffect, useCallback } from "react"
import { Outlet } from "react-router-dom"
import { AnimatePresence, motion } from "framer-motion"
import { useLocation } from "react-router-dom"
import { Header } from "@/components/layout/Header"
import { Sidebar } from "@/components/layout/Sidebar"
import { GodModeBanner } from "@/components/layout/GodModeBanner"
import { AnimatedOrbs } from "@/components/layout/AnimatedOrbs"
import { SupportFAB } from "@/components/support/SupportFAB"
import { SupportWidget } from "@/components/support/SupportWidget"
import { TelemetryProvider } from "@/components/TelemetryProvider"
import { CrashProbe } from "@/components/dev/CrashProbe"
import { useTenant } from "@/hooks/useTenant"
import { useSeletorDoTopo } from "@/hooks/useSeletorDoTopo"
import { useIsMobile } from "@/hooks/useMediaQuery"
import { useScreenshot } from "@/hooks/useScreenshot"
import { useAutomationIveRequestProcessor } from "@/hooks/useAutomationIveRequestProcessor"

export function AppLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [supportOpen, setSupportOpen] = useState(false)
  const [initialScreenshot, setInitialScreenshot] = useState<Blob | null>(null)
  const { isGodMode } = useTenant()
  // A cor da unidade da tela: dentro do Financeiro, a do escopo do módulo (ver
  // `useSeletorDoTopo`), para a borda não ser de uma unidade que lá não aparece.
  const { cor: activeTenantColor } = useSeletorDoTopo()
  const { capture, isCapturing: capturingInitial } = useScreenshot()
  const location = useLocation()
  const isMobile = useIsMobile()

  // Processa requests de recálculo de IVE disparados por automações
  // (task #15 v2 action calculate_ive). Hook escuta Realtime e invoca
  // run-ive-analysis com a sessão do user logado.
  useAutomationIveRequestProcessor()

  // Invariante: cada página carrega com menu mobile fechado.
  // Usamos `location.key` (único por navegação) em vez de `location.pathname`
  // porque o usuário pode clicar no link da rota corrente — pathname não
  // muda mas key sim. Isso garante o fechamento mesmo nesses casos e evita
  // o bug do overlay preso após navegação.
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.key])

  const handleOpenSupport = useCallback(() => {
    // #192: abre o painel NA HORA, sem esperar a captura de tela.
    setInitialScreenshot(null)
    setSupportOpen(true)
    // A captura via html-to-image clona o DOM inteiro — em telas pesadas
    // (o Funil tem ~15k nós + dezenas de imagens cross-origin) ela trava o
    // navegador por dezenas de segundos, mesmo sem bloquear a abertura. Por
    // isso só dispara a captura AUTOMÁTICA em telas leves; nas pesadas o
    // usuário liga a captura na mão (toggle "Incluir captura da tela" no
    // formulário), de forma deliberada. Dois rAF garantem que o painel já
    // pintou antes de iniciar a captura nas telas onde ela roda.
    const heavyDom =
      typeof document !== "undefined" && document.querySelectorAll("*").length > 6000
    if (!heavyDom) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          void capture()
            .then((blob) => setInitialScreenshot(blob))
            .catch(() => setInitialScreenshot(null))
        }),
      )
    }
  }, [capture])

  const handleCloseSupport = useCallback(() => {
    // #193: APENAS fecha. Um segundo setState aqui (ex: limpar o screenshot)
    // re-renderizava o AppLayout durante a animação de saída do drawer e
    // interrompia o AnimatePresence — o overlay `fixed inset-0` ficava preso
    // na tela e travava a página (precisava dar refresh). O screenshot é
    // zerado na próxima abertura.
    setSupportOpen(false)
  }, [])

  return (
    <TelemetryProvider>
    {/* Sonda de quebra proposital — inerte fora do ensaio e sem `?__crash=1` */}
    <CrashProbe />
    <div
      className="relative flex h-screen overflow-hidden bg-[var(--base,#f0f4ff)] flex-col"
    >
      {/* Background orbs */}
      <AnimatedOrbs />

      {/* God mode banner — pushes everything down */}
      <AnimatePresence>
        {isGodMode && <GodModeBanner />}
      </AnimatePresence>

      {/* Offset for god mode banner */}
      <div
        className="relative z-10 flex flex-col h-full"
        style={{ paddingTop: isGodMode ? 40 : 0 }}
      >
        {/* Header */}
        <Header onMenuToggle={() => setMobileMenuOpen(true)} />

        {/* Body: sidebar + main */}
        <div className="flex flex-1 overflow-hidden">
          <Sidebar
            mobileOpen={mobileMenuOpen}
            onMobileClose={() => setMobileMenuOpen(false)}
          />

          {/* Main content with colored border */}
          <div
            className="flex-1 overflow-hidden m-0.5 md:m-1 rounded-xl"
            style={{
              borderWidth: isMobile ? 3 : 1,
              borderStyle: "solid",
              borderColor: activeTenantColor ?? "rgba(255,255,255,0.06)",
              boxShadow: activeTenantColor
                ? `0 0 ${isMobile ? 32 : 24}px ${activeTenantColor}${isMobile ? "24" : "18"}`
                : "none",
              transition: "border-color 0.4s ease, box-shadow 0.4s ease, border-width 0.3s ease",
            }}
          >
            <AnimatePresence mode="wait">
              {/*
                🚨 A CHAVE ERA `location.pathname` INTEIRO, E ISSO DESTRUÍA O
                MÓDULO A CADA TROCA DE ABA.

                Com o caminho completo na chave, ir de /financeiro/dashboard
                para /financeiro/dre conta como "outra página": o AnimatePresence
                desmonta o conteúdo e monta de novo. Só que quem vive aí dentro
                é o `FinanceiroDataProvider` — cujo próprio comentário diz que
                deve ser "montado uma única vez e consumido por todas as abas".

                Resultado: toda troca de aba zerava os dados, voltava tudo para
                "carregando" e refazia as consultas do zero. Era isso que o PM
                via como "o miolo da página fica em branco" (13/08/2026) — e é
                metade da lentidão que ele relatou antes: o cache compartilhado
                do módulo nunca durava mais que um clique.

                A chave passa a ser o MÓDULO (primeiro segmento). Trocar de aba
                dentro do mesmo módulo não anima nem remonta; trocar de módulo
                continua animando como antes. Os três módulos com sub-abas
                (financeiro, configuracoes, super-admin) são todos de abas —
                nenhum é página de detalhe por id que dependa de remontar.
              */}
              <motion.main
                key={`/${location.pathname.split("/")[1] ?? ""}`}
                className="h-full overflow-y-auto p-2 md:p-6"
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <Outlet />
              </motion.main>
            </AnimatePresence>
          </div>
        </div>

        {/* Magical support FAB — visible on all authenticated pages */}
        <SupportFAB
          onOpen={handleOpenSupport}
          isWidgetOpen={supportOpen}
          hidden={mobileMenuOpen}
        />
        <SupportWidget
          open={supportOpen}
          onClose={handleCloseSupport}
          initialScreenshot={initialScreenshot}
          initialScreenshotPending={capturingInitial}
        />
      </div>
    </div>
    </TelemetryProvider>
  )
}
